/**
 * Saved worklog exports (I5): `core.settings.worklogExports[<id>] = {name, spec}`, written the same way as
 * the server's `save_worklog_export`, so they travel with the workspace.
 */
import { LoroMap, type LoroDoc } from "loro-crdt";
import { ulid } from "ulid";
import { commitMessage } from "../core/actor";
import { C } from "../core/schema";

export interface ExportSpec {
  range: "thisMonth" | "lastMonth" | "thisWeek" | "lastWeek" | "custom" | "all";
  fromDay?: string | null;
  toDay?: string | null;
  filter?: Record<string, unknown> | null;
  onlyWithNote: boolean;
  groupBy: "entry" | "task" | "day" | "taskDay" | "group";
  roundTo: 0 | 1 | 6 | 10 | 15 | 30;
  roundMode: "up" | "nearest" | "down";
  roundPer: "entry" | "sum";
  minMinutes: number;
  format: "csv" | "json" | "yaml";
  csvSep: "," | ";" | "tab";
  csvDecimal: "." | ",";
  csvBom: boolean;
}

export const DEFAULT_SPEC: ExportSpec = {
  range: "lastMonth",
  fromDay: null,
  toDay: null,
  filter: null,
  onlyWithNote: false,
  groupBy: "entry",
  roundTo: 0,
  roundMode: "up",
  roundPer: "entry",
  minMinutes: 0,
  format: "csv",
  csvSep: ",",
  csvDecimal: ".",
  csvBom: false,
};

export interface ExportPreset {
  id: string;
  name: string;
  spec: ExportSpec;
}

function presetMap(doc: LoroDoc): LoroMap {
  const settings = doc.getMap(C.settings);
  const cur = settings.get("worklogExports") as unknown;
  if (cur instanceof LoroMap) return cur;
  return settings.setContainer("worklogExports", new LoroMap());
}

export function readPresets(doc: LoroDoc): ExportPreset[] {
  const raw = (doc.getMap(C.settings).toJSON() as { worklogExports?: Record<string, unknown> })
    .worklogExports;
  return Object.entries(raw ?? {})
    .filter(([, v]) => v && typeof v === "object" && (v as { spec?: unknown }).spec)
    .map(([id, v]) => {
      const p = v as { name?: string; spec: Partial<ExportSpec> };
      return { id, name: String(p.name ?? ""), spec: { ...DEFAULT_SPEC, ...p.spec } };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function savePreset(doc: LoroDoc, name: string, spec: ExportSpec, id?: string): string {
  const pid = id ?? ulid();
  presetMap(doc).set(pid, { name: name.trim(), spec: { ...spec } });
  doc.commit({ message: commitMessage({ op: "saveWorklogExport" }) });
  return pid;
}

export function deletePreset(doc: LoroDoc, id: string): void {
  presetMap(doc).delete(id);
  doc.commit({ message: commitMessage({ op: "deleteWorklogExport" }) });
}
